import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Activity, AlertCircle, Loader2 } from "lucide-react";

/**
 * AdminSpendWidget
 *
 * Renders on the Account page ONLY for the email matching backend ADMIN_EMAIL.
 * Surfaces today / last-30-days / all-time AI spend so the founder can sleep
 * at night. Quietly hides itself (returns null) for non-admin users. the
 * server-side 403 is the source of truth.
 */
export default function AdminSpendWidget() {
 const [data, setData] = useState(null);
 const [error, setError] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 let alive = true;
 api.get("/admin/spend")
.then(({ data }) => alive && setData(data))
.catch(e => {
 if (!alive) return;
 // 403 is the expected "you're not admin" response. hide quietly.
 if (e.response?.status !== 403) setError(e.response?.data?.detail || "Failed to load spend");
 })
.finally(() => alive && setLoading(false));
 return () => { alive = false; };
 }, []);

 if (loading) return null;
 if (error) {
 return (
 <div className="card mb-6 border-l-4 border-l-[#B35D4C]" data-testid="admin-spend-error">
 <div className="flex items-center gap-2 text-[#B35D4C] text-sm">
 <AlertCircle size={14} strokeWidth={1.5} /> {error}
 </div>
 </div>
 );
 }
 if (!data) return null; // non-admin

 const fmt = (n) => `$${n.toFixed(4)}`;
 const KIND_LABELS = {
 case_chat: "Compass chat",
 deep_read: "Deep Read (uploads)",
 deep_scan: "Deep Scan (Master File)",
 letter_draft: "Letter drafts",
 letter_refine: "Letter refinements",
 letter_suggest: "Letter tweak suggestions",
 brain_ask: "Tenant Brain (signed in)",
 brain_ask_authed: "Tenant Brain (signed in)",
 brain_ask_anon: "Tenant Brain (anonymous)",
 admin_qa_generate: "Q&A library generation (admin)",
 doc_summary: "Document summaries",
 voice_transcribe: "Voice transcription",
 };
 const kindTotal = data.by_kind_30d.reduce((s, r) => s + (r.cost_usd || 0), 0);

 return (
 <div className="card mb-6 border-l-4 border-l-[#1B6C62]" data-testid="admin-spend-widget">
 <div className="flex items-center gap-2 mb-1">
 <Activity size={16} strokeWidth={1.5} className="text-[#1B6C62]" />
 <h3 className="font-serif text-xl text-[#0D2B52]">AI spend</h3>
 <span className="label-mono text-[10px] text-[#1B6C62]">Admin only</span>
 </div>
 <p className="text-xs text-[#6B7280] mb-4 italic">{data.note}</p>

 <div className="grid grid-cols-3 gap-3 mb-5">
 <Stat label="Today" value={fmt(data.today.cost_usd)} sub={`${data.today.calls} calls`} />
 <Stat label="Last 30 days" value={fmt(data.last_30d.cost_usd)} sub={`${data.last_30d.calls} calls`} />
 <Stat label="All time" value={fmt(data.all_time.cost_usd)} sub={`${data.all_time.calls} calls`} />
 </div>

 {data.by_kind_30d.length > 0 && (
 <div className="mb-5">
 <div className="label-mono mb-2">Burn per feature · last 30d</div>
 <div className="space-y-2">
 {data.by_kind_30d.map(row => {
 const pct = kindTotal > 0 ? Math.round((row.cost_usd / kindTotal) * 100) : 0;
 return (
 <div key={row.kind} className="border-b border-[#E5E4DE] pb-2" data-testid={`spend-kind-${row.kind}`}>
 <div className="flex justify-between text-sm mb-1">
 <span className="text-[#1A1A1A] text-xs">
 {KIND_LABELS[row.kind] || row.kind}
 <span className="ml-2 font-mono text-[10px] text-[#6B7280]">{pct}%</span>
 </span>
 <span className="text-[#6B7280]">
 <span className="font-mono text-[#0D2B52]">{fmt(row.cost_usd)}</span>
 <span className="ml-3 text-xs">{row.calls} calls</span>
 </span>
 </div>
 <div className="w-full bg-[#F0EFEA] h-1 overflow-hidden">
 <div className="h-full bg-[#1B6C62]" style={{ width: `${pct}%` }} />
 </div>
 </div>
 );
 })}
 </div>
 </div>
 )}

 {data.top_users_30d.length > 0 && (
 <div>
 <div className="label-mono mb-2">Top users · last 30d</div>
 <div className="space-y-1.5">
 {data.top_users_30d.map(u => (
 <div key={u.user_id} className="flex justify-between text-sm border-b border-[#E5E4DE] py-1.5">
 <span className="text-[#1A1A1A] truncate font-mono text-xs">{u.email}</span>
 <span className="text-[#6B7280]">
 <span className="font-mono text-[#0D2B52]">{fmt(u.cost_usd)}</span>
 <span className="ml-3 text-xs">{u.calls} calls</span>
 </span>
 </div>
 ))}
 </div>
 </div>
 )}
 </div>
 );
}

function Stat({ label, value, sub }) {
 return (
 <div className="bg-[#FAF9F6] border border-[#E5E4DE] p-3">
 <div className="label-mono mb-1">{label}</div>
 <div className="font-serif text-xl text-[#0D2B52]">{value}</div>
 <div className="text-[10px] text-[#6B7280] font-mono">{sub}</div>
 </div>
 );
}
